"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { formatCurrency } from "@/lib/utils";
import { ImageGallery } from "@/components/car-details/image-gallery";
import { CarDetailsContent } from "@/components/car-details/car-details-content";
import { PriceCard } from "@/components/car-details/price-card";
import { ContactCard } from "@/components/car-details/contact-card";
import { SimilarCars } from "@/components/car-details/similar-cars";
import { ReviewSection } from "@/components/car-details/review-section";
import { isCarSavedByUser, saveCarForUser } from "@/lib/actions/car-details";
import { CarDetails } from "@/lib/actions/car-details";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
	HomeIcon, 
	CarIcon, 
	Heart, 
	AlertCircle, 
	CheckCircle, 
	BatteryCharging, 
	Gauge, 
	Users, 
	ShieldCheck, 
	ArrowUpRight,
	Share2,
	Copy
} from "lucide-react";
import { FaWhatsapp, FaFacebook, FaTwitter, FaLinkedin } from "react-icons/fa";
import { toast } from "sonner";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ShareDialog } from "@/components/car-details/share-dialogue";
import { getCurrentUser } from "@/actions/client-reviews";

interface Props {
	car: CarDetails;
}

export function CarDetailsView({ car }: Props) {
	const router = useRouter();
	const [activeTab, setActiveTab] = useState("features");
	const [isSaved, setIsSaved] = useState(false);
	const [shareModalOpen, setShareModalOpen] = useState(false);
	const [isShareOpen, setIsShareOpen] = useState(false);
	
	// Media
	const carImages = (car.carMedia || [])
		.filter(media => media.type.startsWith("image"))
		.map(media => media.url);
		
	// Pricing and discount calculation
	const price = car.carPricing?.sellingPrice || 0;
	const msrp = car.carPricing?.msrp || 0;
	const discount = msrp > price ? msrp - price : 0;
	const discountPercentage = msrp > 0 ? Math.round((discount / msrp) * 100) : 0;
	
	// Basic car info for display
	const carMake = car.make?.name || '';
	const carModel = car.model?.name || '';
	const carYear = car.year;
	const carTitle = car.title || `${carYear} ${carMake} ${carModel}`;
	
	// Check if car is saved by user
	useEffect(() => {
		const checkSavedStatus = async () => {
			const result = await isCarSavedByUser(car.id);
			if (result.success) {
				setIsSaved(result.isSaved);
			}
		};
		
		checkSavedStatus();
	}, [car.id]);
	
	// Handle save/unsave car
	const handleSaveToggle = async () => {
		const session = await getCurrentUser();
		
		// If user is not logged in, redirect to sign-in page
		if (!session.success || !session.user) {
			// Redirect to sign-in page with callback URL
			window.location.href = `/sign-in?callbackUrl=${encodeURIComponent(window.location.pathname)}`;
			return;
		}
		
		// User is logged in, proceed with save/unsave action
		const result = await saveCarForUser(car.id);
		if (result.success) {
			setIsSaved(result.saved);
			if (result.saved) {
				toast.success("Vehicle saved to your favorites");
			} else {
				toast("Vehicle removed from your favorites");
			}
		} else {
			toast.error("Error updating saved status");
		}
	};
	
	// Extract key info
	const keyFeatures = [
		{ 
			label: "Fuel Type", 
			value: car.carSpecification?.fuelType || "N/A",
			icon: <BatteryCharging className="h-5 w-5 text-primary" />
		},
		{ 
			label: "Transmission", 
			value: car.carSpecification?.transmissionType || "N/A",
			icon: <Gauge className="h-5 w-5 text-primary" />
		},
		{ 
			label: "Mileage", 
			value: car.carSpecification?.mileage ? `${car.carSpecification.mileage} ${car.carSpecification.mileageUnit || 'km'}` : "N/A",
			icon: <CarIcon className="h-5 w-5 text-primary" />
		},
		{ 
			label: "Engine", 
			value: car.carSpecification?.engineCapacity || "N/A",
			icon: <Gauge className="h-5 w-5 text-primary" />
		},
		{ 
			label: "Year", 
			value: carYear.toString(),
			icon: <AlertCircle className="h-5 w-5 text-primary" />
		},
		{ 
			label: "Condition", 
			value: car.condition,
			icon: <CheckCircle className="h-5 w-5 text-primary" />
		},
		{ 
			label: "Seating", 
			value: car.carSpecification?.seats ? `${car.carSpecification.seats} seats` : "N/A",
			icon: <Users className="h-5 w-5 text-primary" />
		},
		{ 
			label: "Warranty", 
			value: car.carPricing?.warranty || "N/A",
			icon: <ShieldCheck className="h-5 w-5 text-primary" />
		}
	];

	// Share functionality
	const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
	const shareText = `Check out this ${carYear} ${carMake} ${carModel}`;

	const handleCopyLink = () => {
		navigator.clipboard.writeText(shareUrl);
		toast.success("Link copied to clipboard");
	};

	const handleShare = (platform: string) => {
		let shareLink = '';
		
		switch (platform) {
			case 'facebook':
				shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
				break;
			case 'twitter':
				shareLink = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
				break;
			case 'linkedin':
				shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
				break;
			case 'whatsapp':
				shareLink = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
				break;
			default:
				return;
		}
		
		window.open(shareLink, '_blank');
	};

	return (
		<div className="animate-in fade-in-50 duration-500">
				{/* Breadcrumb */}
			<nav className="flex items-center text-sm text-muted-foreground mb-6">
				<Link href="/" className="flex items-center hover:text-primary transition-colors">
					<HomeIcon className="h-3.5 w-3.5 mr-1" />
					<span>Home</span>
						</Link>
				<span className="mx-2 text-muted-foreground/50">/</span>
				<Link href="/cars" className="hover:text-primary transition-colors">
							Cars
						</Link>
				<span className="mx-2 text-muted-foreground/50">/</span>
				<Link href={`/cars/make/${car.make?.slug || ''}`} className="hover:text-primary transition-colors">
					{carMake}
				</Link>
				<span className="mx-2 text-muted-foreground/50">/</span>
				<Link href={`/cars/model/${car.model?.slug || ''}`} className="hover:text-primary transition-colors">
					{carModel}
				</Link>
			</nav>

			{/* Title section with action buttons */}
			<div className="mb-8">
				<div className="flex flex-wrap items-center gap-2 mb-3">
					<Badge 
						variant="outline" 
						className="bg-primary/10 text-primary border-primary/20 backdrop-blur-sm font-medium"
					>
						{car.condition}
					</Badge>
					
					{car.featured && (
						<Badge
							variant="outline" 
							className="bg-purple-500/10 text-purple-500 border-purple-500/20 backdrop-blur-sm font-medium"
						>
							Featured
						</Badge>
					)}
					
					{car.bigDeal && (
							<Badge
							variant="outline" 
							className="bg-amber-500/10 text-amber-500 border-amber-500/20 backdrop-blur-sm font-medium"
							>
								Big Deal
							</Badge>
						)}
					
					{discount > 0 && (
							<Badge
							variant="outline" 
							className="bg-green-500/10 text-green-500 border-green-500/20 backdrop-blur-sm font-medium"
						>
							{discountPercentage}% Off
						</Badge>
					)}
					
					<div className="ml-auto flex gap-2">
						<Dialog open={isShareOpen} onOpenChange={setIsShareOpen}>
							<DialogTrigger asChild>
								<Button 
									variant="outline" 
									size="icon" 
									className="rounded-full bg-muted/50 hover:bg-primary/10"
								>
									<Share2 className="h-4 w-4 text-muted-foreground" />
								</Button>
							</DialogTrigger>
							<ShareDialog open={isShareOpen} onOpenChange={setIsShareOpen} title={carTitle} url={shareUrl} />
						</Dialog>
						
						<Button 
							variant={isSaved ? "default" : "outline"} 
							size="sm" 
							className={`h-8 gap-1.5 ${isSaved ? 'bg-primary text-primary-foreground' : 'border-primary/20 hover:bg-primary/10'} hover-glow`}
							onClick={handleSaveToggle}
						>
							<Heart className={`h-3.5 w-3.5 ${isSaved ? 'fill-current' : ''}`} />
							<span className="hidden sm:inline">{isSaved ? 'Saved' : 'Save'}</span>
						</Button>
					</div>
				</div>

				<h1 className="text-3xl font-bold text-foreground mb-2 leading-tight">
					{carTitle}
				</h1>
				
				<p className="text-muted-foreground line-clamp-2">
					{car.description || `${carYear} ${carMake} ${carModel} in ${car.condition} condition.`}
				</p>
							</div>

			{/* Main content */}
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
				{/* Left Column: Gallery and Details */}
				<div className="lg:col-span-2 space-y-8">
					<ImageGallery images={carImages} />
					
					{/* Key Info Cards */}
					<div className="relative rounded-xl overflow-hidden glossy-card">
						<div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
						<div className="absolute inset-0 rounded-xl border border-primary/10 neon-glow"></div>
						
						<div className="p-6 relative z-10">
							<h2 className="text-xl font-semibold mb-4 text-foreground">
								Key Features
							</h2>
							
							<div className="grid grid-cols-2 md:grid-cols-4 gap-6">
								{keyFeatures.map((feature, index) => (
									<div key={index} className="flex items-start gap-3">
										<div className="relative rounded-full p-2 bg-primary/5 neon-glow">
											{feature.icon}
										</div>
										<div>
											<p className="text-xs text-muted-foreground">{feature.label}</p>
											<p className="font-medium">{feature.value}</p>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>

					{/* Main tabs content */}
					<div>
						<Tabs defaultValue="features" value={activeTab} onValueChange={setActiveTab}>
							<TabsList className="w-full grid grid-cols-4 bg-background border-b border-muted mb-6 sticky top-0 z-10 backdrop-blur-sm">
								<TabsTrigger value="features" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary">
									Features
								</TabsTrigger>
								<TabsTrigger value="specs" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary">
									Specifications
								</TabsTrigger>
								<TabsTrigger value="ownership" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary">
									Ownership
								</TabsTrigger>
								<TabsTrigger value="documents" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-primary">
									Documents
								</TabsTrigger>
							</TabsList>
							
							<CarDetailsContent car={car} activeTab={activeTab} />
						</Tabs>
					</div>
				</div>
				
				{/* Right Column: Pricing and Contact */}
				<div className="space-y-6">
					<PriceCard car={car} />
					<ContactCard organization={car.organization} car={car} />
					<SimilarCars 
						currentCarId={car.id} 
						makeId={car.make?.id} 
						modelId={car.model?.id} 
						typeId={car.type?.id} 
					/>
				</div>
			</div>

			{/* Reviews Section */}
			<div className="mt-16">
				<ReviewSection carId={car.id} />
			</div>
		</div>
	);
}
